import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './App.css';

function ChatInterface() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Add click handlers to NCT links
    const handleNctLinkClick = (e) => {
      if (e.target.classList.contains('nct-link')) {
        e.preventDefault();
        const nctId = e.target.getAttribute('data-nct');
        
        // Inline the click handling to avoid dependency issues
        const userMessage = `Tell me more about ${nctId}`;
        setIsLoading(true);
        
        const newUserMessage = {
          role: 'user',
          content: userMessage
        };
        setMessages(prev => [...prev, newUserMessage]);

        axios.post('https://trial-atlas-api.onrender.com/chat', {
          message: userMessage,
          conversation_history: [...messages, newUserMessage]
        })
        .then(response => {
          const assistantMessage = {
            role: 'assistant',
            content: response.data.response,
            toolUsed: response.data.tool_used
          };
          setMessages(prev => [...prev, assistantMessage]);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error:', error);
          const errorMessage = {
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.'
          };
          setMessages(prev => [...prev, errorMessage]);
          setIsLoading(false);
        });
      }
    };

    document.addEventListener('click', handleNctLinkClick);
    return () => document.removeEventListener('click', handleNctLinkClick);
  }, [messages]);

  const formatAssistantMessage = (text) => {
  // Remove multiple newlines and clean up spacing
  let formatted = text.trim();
  
  // Remove ALL hashtags (##, ###, etc.) and clean up the text
  formatted = formatted.replace(/^#{1,6}\s+/gm, '');
  
  // Step 1: Protect URLs by temporarily replacing them with placeholders
  const urls = [];
  formatted = formatted.replace(/(https?:\/\/[^\s]+)/g, (match) => {
    urls.push(match);
    return `___URL_${urls.length - 1}___`;
  });
  
  // Step 1.5: Protect tel: links (phone numbers) 
  const telLinks = [];
  formatted = formatted.replace(/<a href="tel:([^"]+)">([^<]+)<\/a>/g, (match, telNumber, displayNumber) => {
    telLinks.push({ tel: telNumber, display: displayNumber });
    return `___TEL_${telLinks.length - 1}___`;
  });

  // Step 2: Now do markdown formatting (URLs are protected)
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/\*(.*?)\*/g, '$1');
  
  // Step 3: Convert NCT IDs to clickable spans (URLs are still placeholders, so this is safe)
  formatted = formatted.replace(/\b(NCT\d{8})\b/g, '<span class="nct-link" data-nct="$1">$1</span>');

  // Step 3.5: Convert phone numbers to clickable tel: links
  formatted = formatted.replace(/(\d{3}[-.]?\d{3}[-.]?\d{4})/g, (match) => {
  const cleanPhone = match.replace(/[-.]/g, '');
    return `<a href="tel:+1${cleanPhone}" class="phone-link">${match}</a>`;
  });

  // Step 4: Restore URLs as proper links
  formatted = formatted.replace(/___URL_(\d+)___/g, (match, index) => {
    return `<a href="${urls[index]}" target="_blank" rel="noopener noreferrer" class="external-link">${urls[index]}</a>`;
  });
  
  // Step 4.5: Restore tel: links
  formatted = formatted.replace(/___TEL_(\d+)___/g, (match, index) => {
  const link = telLinks[index];
    return `<a href="tel:${link.tel}" class="phone-link">${link.display}</a>`;
  });

  // Step 5: Replace newlines with <br> tags
  formatted = formatted.replace(/\n/g, '<br>');
  
  // Render as HTML
  return <div dangerouslySetInnerHTML={{ __html: formatted }} />;
};

  const handleSend = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    // Add user message to chat
    const newUserMessage = {
      role: 'user',
      content: userMessage
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      const response = await axios.post('https://trial-atlas-api.onrender.com/chat', {
        message: userMessage,
        conversation_history: messages
      });

      // Add assistant message
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        toolUsed: response.data.tool_used
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setInputMessage('');
  };

  return (
    <div className="App">
      {/* Header */}
      <div className="app-header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" className="logo-link">
              <img src="/trial-atlas-logo-white.png" alt="Trial Atlas" className="header-logo" />
            </Link>
          </div>
          <button className="new-chat-button" onClick={handleNewChat}>
             New Chat
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="messages-container">
        {messages.length === 0 && (
          <div className="message-wrapper">
            <div className="message-header">
              <div className="assistant-icon">
                <img src="/trial-atlas-icon.png" alt="TA" className="assistant-icon-img" />
              </div>
              <span className="message-label">Trial Atlas</span>
            </div>
            <div className="message-content assistant-message">
              <p><strong>Welcome to Trial Atlas!</strong></p>
              <p>I can help you search for clinical trials. To get started, please tell me:</p>
              <p>• What medical condition are you interested in?</p>
              <p>• What location are you searching in?</p>
              <p>• What is the patient's age?</p>
            </div>
          </div>
        )}

        {messages.map((message, index) => (
          <div key={index} className="message-wrapper">
            <div className="message-header">
              {message.role === 'user' ? (
                <>
                  <div className="user-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                </>
              ) : (
                <>
                  <div className="assistant-icon">
                    <img src="/trial-atlas-icon.png" alt="TA" className="assistant-icon-img" />
                  </div>
                  <span className="message-label">Trial Atlas</span>
                </>
              )}
            </div>
            
            {message.toolUsed && (
              <div className="tool-usage">
                <span className="tool-usage-icon">🔍</span>
                Searching For Trials...
              </div>
            )}
            
            <div className={`message-content ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}>
              {message.role === 'user' ? (
                <p>{message.content}</p>
              ) : (
                formatAssistantMessage(message.content)
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="message-wrapper">
            <div className="message-header">
              <div className="assistant-icon">
                <img src="/trial-atlas-icon.png" alt="TA" className="assistant-icon-img" />
              </div>
              <span className="message-label">Trial Atlas</span>
            </div>
            <div className="message-content assistant-message">
              <div className="typing-indicator">
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="input-container">
        <div className="input-wrapper">
          <input
            type="text"
            className="message-input"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about clinical trials..."
            disabled={isLoading}
          />
          <button
            className="send-button"
            onClick={handleSend}
            disabled={isLoading || !inputMessage.trim()}
            title="Send message"
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatInterface;